import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  close as closeSideNavigation,
  updateParams,
} from "@store/features/layout/sideNavigationSlice";
import {
  addStage,
  updateStage,
  type StageFormState,
} from "@store/features/agents/agentSlice";
import { toast } from "sonner";
import { SpanCol } from "skedyul-ui/ui";
import type {
  SchemaField,
  SchemaFieldType,
  ToolBinding,
  AvailableToolOption,
} from "skedyul-ui/ui";

export type StageType = "agent" | "tool" | "transform" | "output";

export type EditStageParams = {
  agentId: string;
  stageIndex?: number;
  stage: StageFormState;
  isNew?: boolean;
  llmModels?: Array<{ value: string; label: string }>;
  /** Available tools for the dropdown */
  availableTools?: AvailableToolOption[];
  /** Working copy of output schema fields for the UI */
  outputSchemaFields?: SchemaField[];
  /** Working copy of tool bindings for the UI */
  toolBindings?: ToolBinding[];
};

function generateHandle(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getParams(): EditStageParams {
  const state = store.getState();
  return state.sideNavigation.right.params as EditStageParams;
}

function updateStageField<K extends keyof StageFormState>(
  key: K,
  value: StageFormState[K],
) {
  const currentParams = getParams();
  store.dispatch(
    updateParams({
      position: "right",
      params: {
        ...currentParams,
        stage: {
          ...currentParams.stage,
          [key]: value,
        },
      },
    }),
  );
}

function updateAgentField<K extends keyof NonNullable<StageFormState["props"]>>(
  key: K,
  value: NonNullable<StageFormState["props"]>[K],
) {
  const currentParams = getParams();
  const currentAgent = currentParams.stage.props ?? {
    handle: "",
    name: "",
    system: "",
  };
  store.dispatch(
    updateParams({
      position: "right",
      params: {
        ...currentParams,
        stage: {
          ...currentParams.stage,
          props: {
            ...currentAgent,
            [key]: value,
          },
        },
      },
    }),
  );
}

function updateStageName(name: string) {
  const currentParams = getParams();
  const handle = generateHandle(name);
  const currentAgent = currentParams.stage.props ?? {
    handle: "",
    name: "",
    system: "",
  };

  store.dispatch(
    updateParams({
      position: "right",
      params: {
        ...currentParams,
        stage: {
          ...currentParams.stage,
          id: handle,
          props: {
            ...currentAgent,
            handle,
            name,
          },
        },
      },
    }),
  );
}

// Convert output schema from Redux state to SchemaField array
// Handles both JSON Schema format ({ type: 'object', properties: {...} })
// and flat format ({ fieldName: { type: 'string', ... } })
function outputSchemaToFields(
  outputSchema: Record<string, unknown> | undefined,
): SchemaField[] {
  if (!outputSchema) return [];

  // Check if it's JSON Schema format with type: 'object' and properties
  if (outputSchema.type === "object" && outputSchema.properties) {
    const properties = outputSchema.properties as Record<string, unknown>;
    return Object.entries(properties).map(([key, value]) => {
      return convertPropertyToField(key, value as Record<string, unknown>);
    });
  }

  // Flat format: keys are field names, values are field configs
  return Object.entries(outputSchema).map(([key, value]) => {
    return convertPropertyToField(key, value as Record<string, unknown>);
  });
}

function convertPropertyToField(
  key: string,
  config: Record<string, unknown>,
): SchemaField {
  const id = `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const field: SchemaField = {
    id,
    key,
    type: (config.type as SchemaFieldType) || "string",
    description: config.description as string | undefined,
  };

  if (config.enum) {
    field.type = "enum";
    field.enumValues = config.enum as string[];
  }

  if (config.type === "array" && config.items) {
    const items = config.items as Record<string, unknown>;
    field.arrayItemType = (items.type as SchemaFieldType) || "string";
    if (items.type === "object" && items.properties) {
      field.nestedFields = outputSchemaToFields({
        type: "object",
        properties: items.properties,
      });
    }
  }

  if (config.type === "object" && config.properties) {
    field.nestedFields = outputSchemaToFields({
      type: "object",
      properties: config.properties,
    });
  }

  return field;
}

// Convert SchemaField array back to output schema format (JSON Schema format)
function fieldsToOutputSchema(
  fields: SchemaField[],
): Record<string, unknown> | undefined {
  if (fields.length === 0) return undefined;

  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  for (const field of fields) {
    // Skip fields with empty keys
    if (!field.key || field.key.startsWith("__empty_")) continue;

    const fieldSchema: Record<string, unknown> = {
      type: field.type === "enum" ? "string" : field.type,
    };

    if (field.description) {
      fieldSchema.description = field.description;
    }

    if (field.type === "enum" && field.enumValues?.length) {
      fieldSchema.enum = field.enumValues;
    }

    if (field.type === "array") {
      const itemType = field.arrayItemType || "string";
      if (itemType === "object" && field.nestedFields?.length) {
        const nestedSchema = fieldsToOutputSchema(field.nestedFields);
        fieldSchema.items = {
          type: "object",
          properties: nestedSchema?.properties || {},
        };
      } else {
        fieldSchema.items = { type: itemType };
      }
    }

    if (field.type === "object" && field.nestedFields?.length) {
      const nestedSchema = fieldsToOutputSchema(field.nestedFields);
      fieldSchema.properties = nestedSchema?.properties || {};
    }

    properties[field.key] = fieldSchema;
    required.push(field.key);
  }

  if (Object.keys(properties).length === 0) return undefined;

  return {
    type: "object",
    properties,
    required,
  };
}

function updateOutputSchema(fields: SchemaField[]) {
  const currentParams = getParams();
  const outputSchema = fieldsToOutputSchema(fields);
  const currentAgent = currentParams.stage.props ?? {
    handle: "",
    name: "",
    system: "",
  };

  store.dispatch(
    updateParams({
      position: "right",
      params: {
        ...currentParams,
        outputSchemaFields: fields,
        stage: {
          ...currentParams.stage,
          props: {
            ...currentAgent,
            outputSchema,
          },
        },
      },
    }),
  );
}

// Convert tools array from stage to ToolBinding array
function toolsToBindings(
  tools:
    | Array<
        | string
        | {
            tool: string;
            handle?: string;
            name?: string;
            description?: string;
            requiresApproval?: boolean;
          }
      >
    | undefined,
): ToolBinding[] {
  if (!tools) return [];

  return tools.map((tool) => {
    const id = `tool-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    if (typeof tool === "string") {
      return { id, tool };
    }
    return {
      id,
      tool: tool.tool,
      name: tool.name,
      description: tool.description,
      executionMode: tool.requiresApproval ? "REQUIRE_APPROVAL" : undefined,
    };
  });
}

// Convert ToolBinding array back to tools format
function bindingsToTools(bindings: ToolBinding[]):
  | Array<
      | string
      | {
          tool: string;
          name?: string;
          description?: string;
          requiresApproval?: boolean;
        }
    >
  | undefined {
  if (bindings.length === 0) return undefined;

  return bindings
    .filter((b) => b.tool) // Skip empty tool identifiers
    .map((binding) => {
      const requiresApproval = binding.executionMode === "REQUIRE_APPROVAL";
      if (!binding.name && !binding.description && !requiresApproval) {
        return binding.tool;
      }
      return {
        tool: binding.tool,
        ...(binding.name && { name: binding.name }),
        ...(binding.description && { description: binding.description }),
        ...(requiresApproval && { requiresApproval: true }),
      };
    });
}

function updateToolBindings(bindings: ToolBinding[]) {
  const currentParams = getParams();
  const tools = bindingsToTools(bindings);
  const currentAgent = currentParams.stage.props ?? {
    handle: "",
    name: "",
    system: "",
  };

  store.dispatch(
    updateParams({
      position: "right",
      params: {
        ...currentParams,
        toolBindings: bindings,
        stage: {
          ...currentParams.stage,
          props: {
            ...currentAgent,
            tools,
          },
        },
      },
    }),
  );
}

export const editStageSchema = (
  args: SideNavigationComponentProps<PageV2Component, EditStageParams>,
): SideNavigationV2Props<PageV2Component> => {
  const {
    sideNavigation: {
      right: { isOpen, params },
    },
  } = args.state;

  const stageParams = params as EditStageParams | null;
  const stage = stageParams?.stage;
  const isNew = stageParams?.isNew ?? false;
  const llmModels = stageParams?.llmModels ?? [];
  const availableTools = stageParams?.availableTools ?? [];

  // Use outputSchemaFields from params if available, otherwise convert from outputSchema
  const outputSchemaFields =
    stageParams?.outputSchemaFields ??
    outputSchemaToFields(
      stage?.props?.outputSchema as Record<string, unknown> | undefined,
    );

  // Use toolBindings from params if available, otherwise convert from props.tools
  const toolBindings =
    stageParams?.toolBindings ?? toolsToBindings(stage?.props?.tools);

  const stageName = stage?.props?.name || stage?.id || "";

  const handleSave = () => {
    const currentParams = getParams();
    const stageToSave = currentParams.stage;

    if (stageToSave.type === "agent") {
      if (!stageToSave.props?.name) {
        toast.error("Stage name is required");
        return;
      }
      if (!stageToSave.props?.system) {
        toast.error("System prompt is required");
        return;
      }
    }

    if (stageToSave.type === "tool" && !stageToSave.resource) {
      toast.error("Tool resource is required");
      return;
    }

    if (currentParams.isNew) {
      store.dispatch(addStage(stageToSave));
      toast.success(
        `Stage "${stageToSave.props?.name || stageToSave.id}" added`,
      );
    } else if (currentParams.stageIndex !== undefined) {
      store.dispatch(
        updateStage({ index: currentParams.stageIndex, stage: stageToSave }),
      );
      toast.success(
        `Stage "${stageToSave.props?.name || stageToSave.id}" updated`,
      );
    }

    store.dispatch(closeSideNavigation({ position: "right" }));
  };

  const stageTypeOptions = [
    { value: "agent", label: "Agent - Run an LLM agent" },
    { value: "tool", label: "Tool - Execute a system tool" },
  ];

  return {
    side: "right",
    isOpen,
    width: "600px",
    component: {
      component: "PageV2",
      props: {
        title: isNew ? "Add Stage" : `Edit: ${stageName}`,
        hideTitle: true,
        header: {
          icon: stage?.type === "tool" ? "Wrench" : "Bot",
          primaryAction: {
            label: isNew ? "Add Stage" : "Save Changes",
            handleClick: handleSave,
          },
        },
        content: {
          structure: "sidebar",
          components: [
            // Stage Configuration
            {
              type: "Card",
              title: "Stage Configuration",
              props: {
                header: {
                  title: "Stage Configuration",
                },
                form: {
                  formVersion: "v1",
                  id: "stage-config-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text",
                        id: "stage-name",
                        label: "Stage Name",
                        leftIcon: "Bot",
                        value: stage?.props?.name || "",
                        placeholder: "e.g., Clarify Intent, Generate Response",
                        helpText: stage?.id
                          ? `Handle: ${stage.id}`
                          : "Enter a name to generate the handle",
                        handleChange: (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          updateStageName(e.target.value);
                        },
                      },
                    },
                    {
                      component: "Select",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "stage-type",
                        label: "Type",
                        value: stage?.type || "agent",
                        items: stageTypeOptions,
                        isDisabled: !isNew,
                        handleChange: (value: string) => {
                          const newType = value as StageType;
                          updateStageField("type", newType);
                          if (newType === "agent" && !stage?.props) {
                            updateStageField("props", {
                              handle: "",
                              name: "",
                              system: "",
                            });
                          }
                        },
                      },
                    },
                  ],
                },
              },
            },
            // Tool Configuration (for tool stages)
            {
              type: "Card",
              title: "Tool Configuration",
              hidden: stage?.type !== "tool",
              props: {
                header: {
                  title: "Tool Configuration",
                },
                form: {
                  formVersion: "v1",
                  id: "tool-config-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text",
                        id: "tool-resource",
                        label: "Tool Resource",
                        leftIcon: "Wrench",
                        value: stage?.resource || "",
                        placeholder: "e.g., system:crm:prospect:get",
                        helpText: "The tool identifier to execute",
                        handleChange: (
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          updateStageField("resource", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            // System Prompt (for agent stages)
            {
              type: "Card",
              title: "System Prompt",
              hidden: stage?.type !== "agent",
              props: {
                header: {
                  title: "System Prompt",
                  description: "Define the agent's behavior and capabilities",
                },
                form: {
                  formVersion: "v1",
                  id: "system-prompt-form",
                  fields: [
                    {
                      component: "Textarea",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-system",
                        value: stage?.props?.system || "",
                        placeholder: "You are a helpful assistant that...",
                        rows: 6,
                        handleChange: (
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          updateAgentField("system", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            // Model (for agent stages)
            {
              type: "Card",
              title: "Model",
              hidden: stage?.type !== "agent",
              props: {
                header: {
                  title: "Model",
                },
                form: {
                  formVersion: "v1",
                  id: "model-form",
                  fields: [
                    {
                      component: "Select",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "agent-model",
                        label: "LLM Model",
                        placeholder: "Default model",
                        value: stage?.props?.llmModelId || "",
                        items: llmModels,
                        handleChange: (value: string) => {
                          updateAgentField("llmModelId", value || undefined);
                        },
                      },
                    },
                  ],
                },
              },
            },
            // Tools (for agent stages)
            {
              type: "Card",
              title: "Tools",
              hidden: stage?.type !== "agent",
              props: {
                header: {
                  title: "Tools",
                  description: "Configure which tools this agent can use",
                },
                form: {
                  formVersion: "v1",
                  id: "tools-form",
                  fields: [
                    {
                      component: "ToolBindingBuilder",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "tool-bindings",
                        value: toolBindings,
                        onChange: updateToolBindings,
                        availableTools: availableTools,
                        emptyMessage:
                          "No tools configured. The agent won't be able to use any tools.",
                        addLabel: "Add Tool",
                        helpText:
                          "Add tools the agent can use. Expand each tool to customize its name and description.",
                      },
                    },
                  ],
                },
              },
            },
            // Stage Prompt (for agent stages)
            {
              type: "Card",
              title: "Stage Prompt",
              hidden: stage?.type !== "agent",
              props: {
                header: {
                  title: "Stage Prompt",
                  description: "Optional - supports Liquid templates",
                },
                form: {
                  formVersion: "v1",
                  id: "stage-prompt-form",
                  fields: [
                    {
                      component: "Textarea",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "stage-prompt",
                        value: stage?.prompt || "",
                        placeholder:
                          "Leave empty for auto-generated prompt, or use Liquid templates:\n\n{% if input.condition %}\n  Custom prompt here\n{% endif %}",
                        helpText:
                          "If empty, previous stage outputs are automatically forwarded.",
                        rows: 4,
                        handleChange: (
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          updateStageField("prompt", e.target.value);
                        },
                      },
                    },
                  ],
                },
              },
            },
            // Output Schema (for agent stages)
            {
              type: "Card",
              title: "Output Schema",
              hidden: stage?.type !== "agent",
              props: {
                header: {
                  title: "Output Schema",
                  description:
                    "Define the structured output the agent should return",
                },
                form: {
                  formVersion: "v1",
                  id: "output-schema-form",
                  fields: [
                    {
                      component: "SchemaBuilder",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "output-schema",
                        value: outputSchemaFields,
                        onChange: updateOutputSchema,
                        emptyMessage:
                          "No output fields defined. The agent will return unstructured text.",
                        addLabel: "Add Output Field",
                        helpText:
                          "Define fields to get structured JSON output from the agent.",
                        maxDepth: 1,
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  };
};
